// src/oura/newscan/fixtures/dedupe.ts
//
// The generation-time dedupe pass: once the 50 are rendered and before the
// decisions are computed, every exact or near repeat (the lint's own
// comparison, tooSimilar) is settled by Oura where it can be, so staff only
// see the repeats that need them.
//
// Rule, per duplicate pair the lint reports:
//   - change the LATER row; when the later one is a location row and the
//     earlier one is city-level, change the city-level row instead
//   - never a locked, reworded or "try another" row, never a row with a blank
//     word (its missing_word decision comes first), never a non-English row,
//     never a row still on an old recipe's wording
//   - template candidates: another template of the same group and check
//     (alternatives.ts templatePool), one no other row is asked with, so the
//     counts never move (26/14/10, 14/20/16)
//   - a template candidate keeps the row's subject: its main word has the
//     same value as the row's ("lower back pain treatment" stays lower back
//     pain treatment, never the condition "slipped disc"); a row about the
//     place itself stays about the place. Only the word fallback below may
//     change the subject
//   - we go to them: never a template that assumes customers travel to the
//     place (people travelling across town, walk-ins, opening hours, a
//     building, station or landmark); the home-visit swaps (HOME_SWAPS,
//     HOME_CITY_SWAPS) still apply
//   - a candidate passes when the row then has no lint issue of its own,
//     nothing repeats it, the set has fewer repeats, no new error or warning,
//     and it does not ask the same thing as another row in other words
//     (sameMeaning below)
//   - of the candidates that pass, the one whose HIGHEST similarity to any
//     other row is lowest wins (ties: recipe order)
//   - no template passes: the word fallback, the same sentence with the row's
//     main word swapped for the value of ANOTHER variable of its family
//     (another secondary_service_N, another audience_N, another pain point)
//     that no other row uses, same checks, same ranking. Never the word's own
//     alternatives: those are synonyms by design ("after work" / "after
//     8pm"), so the swap would ask the same question again
//   - nothing passes: the pair is left for the duplicate decision
//
// Runs on generation, on Draft again and in a set_town answer's no-AI rebuild
// (store.ts redraft), never on a staff edit: a staff reword or word edit that
// makes a repeat still raises the decision.
// A read re-renders the stored result and never runs the pass.
// Each change is stored as { tpl?, values?, from } on DraftState.auto and
// shown as the auto_reworded note.

import type { AssembledRow, AutoReword, DraftState } from "./model";
import { rowCtx, templatePool } from "./alternatives";
import { assemble, type Assembly } from "./assemble";
import { contentTokens, errorId, similarity, type LintIssue } from "./lint";
import { familyMembers, HOME_CITY_SWAPS, PLACE_VARS, templateById, templateVars, wordFamily } from "./recipe";

/** We go to them: templates that assume the customer comes to the place. */
const VISIT_ONLY = /\b(travel|walk-ins?|open)\b|\{(area_building|area_transit|area_nearby_landmark)\}/i;

/** Enough rounds for every pair in a set of 50; each round settles or gives up on one pair. */
const MAX_ROUNDS = 50;

const isDup = (i: LintIssue) => i.code === "duplicate";
const pairId = (i: LintIssue) => `${i.other ?? 0}|${i.key}`;

function changeable(state: DraftState, row: AssembledRow): boolean {
    const ov = state.rows[row.plan.key];
    const q = row.question;
    if (q.locked || q.reworded || q.swapped || ov?.tpl || ov?.values) return false;
    if (row.plan.language !== "en" || row.missing.length) return false;
    return !state.oldKeys.includes(row.plan.key);
}

/** Template ids the set already asks with (a home-visit swap counts as its base slot too). */
function usedTemplates(state: DraftState, a: Assembly): Set<string> {
    const used = new Set<string>();
    a.rows.forEach((r) => {
        if (r.plan.check !== "location") used.add(r.plan.key);
        const ov = state.rows[r.plan.key];
        if (ov?.tpl) used.add(ov.tpl);
    });
    Object.values(state.auto ?? {}).forEach((x) => {
        if (x.tpl) used.add(x.tpl);
    });
    Object.entries(HOME_CITY_SWAPS).forEach(([base, swap]) => {
        if (used.has(base)) used.add(swap.id);
    });
    return used;
}

/** Whether `after` settled `row`'s repeat without making anything else worse. */
function better(before: Assembly, after: Assembly, key: string, n: number): boolean {
    if (after.issues.some((i) => i.key === key || (isDup(i) && i.other === n))) return false;
    if (after.issues.filter(isDup).length >= before.issues.filter(isDup).length) return false;
    const hadErrors = new Set(before.issues.filter((i) => i.severity === "error").map(errorId));
    if (after.issues.some((i) => i.severity === "error" && !hadErrors.has(errorId(i)))) return false;
    return after.issues.filter((i) => !isDup(i)).length <= before.issues.filter((i) => !isDup(i)).length;
}

/* ------------------------------------------------------------------------- *
 * Same thing in other words
 * ------------------------------------------------------------------------- */

/** Words that change no meaning between two questions ("top rated" vs "top ... right now"). */
const INTERCHANGEABLE_PHRASES = /\b(top rated|highest rated|right now)\b/g;
const INTERCHANGEABLE = new Set(["best", "top", "recommended", "recommend", "most", "good", "great", "popular"]);
/** Openers and fillers on top of the lint's stop words. */
const OPENERS = new Set(["which", "what", "how", "do", "does", "should", "people", "there", "any", "of", "with", "has", "have", "that", "go", "you"]);

/** "clinics" and "clinic" are one word here. */
const singular = (w: string) => (w.length > 3 && w.endsWith("s") && !/(ss|us|is)$/.test(w) ? w.slice(0, -1) : w);

/** The words left once openers, stop words and interchangeable words are gone. */
export function meaningWords(text: string): Set<string> {
    const words = contentTokens(text.toLowerCase().replace(INTERCHANGEABLE_PHRASES, " "));
    const out = new Set<string>();
    words.forEach((w) => {
        if (!INTERCHANGEABLE.has(w) && !OPENERS.has(w)) out.add(singular(w));
    });
    return out;
}

/**
 * Words that change the tone of a question, not what it asks: a set that holds
 * another plus only these asks the same thing ("Which physio clinic in Subang
 * Jaya do locals rate best?" vs "Best physio clinic in Subang Jaya?").
 */
const FILLER = new Set(["try", "local", "rate", "rated", "rating", "highest", "review", "reviewed", "now", "right", "really", "actually", "currently", "today", "known", "famous", "trusted", "reliable", "people"]);

/** `big` holds every word of `small`, and its other words are only fillers. */
const holds = (big: Set<string>, small: Set<string>) => {
    let ok = true;
    small.forEach((w) => {
        if (!big.has(w)) ok = false;
    });
    big.forEach((w) => {
        if (!small.has(w) && !FILLER.has(w)) ok = false;
    });
    return ok;
};

/**
 * Two questions that ask the same thing in other words: their meaning words
 * are equal, or one set holds the other plus fillers only. "What are the top
 * rated physio clinics in Subang Jaya?" and "What are the top physio clinics
 * in Subang Jaya right now?" both come down to {physio, clinic, subang, jaya}.
 * A set that adds a real word ("for runners", "travel across town") asks more,
 * so it is another question: without that, "Best physio clinic in Subang
 * Jaya?" would match every "Which physio clinic in Subang Jaya ..." row.
 */
export function sameMeaning(a: string, b: string): boolean {
    const x = meaningWords(a);
    const y = meaningWords(b);
    if (!x.size || !y.size) return false;
    return x.size >= y.size ? holds(x, y) : holds(y, x);
}

/* ------------------------------------------------------------------------- *
 * Candidates
 * ------------------------------------------------------------------------- */

/** Words that only name the place or the thing: another pick is a synonym, the same question again. */
const NAME_WORDS = new Set(["place_noun", "store_noun", "product_noun", "category", "alternative_category"]);

/**
 * The word fallback's values for a row: the values of the OTHER variables of
 * its main word's family, none any other row uses. Never the word's own
 * alternatives (synonyms by design: "desk workers" / "office workers").
 */
function wordCandidates(a: Assembly, row: AssembledRow, prior: AutoReword | undefined): Array<Record<string, string>> {
    const tpl = (prior?.tpl && templateById(prior.tpl)) || row.plan.template;
    const main = mainVar(row, templateVars(tpl.text));
    if (!main) return [];
    const current = (prior?.values?.[main] ?? a.src.value(main)).toLowerCase();
    const others = a.rows.filter((r) => r !== row).map((r) => r.question.text.toLowerCase());
    // A naming word's value (the category, "physiotherapy") is never a service: it would turn the row into the category question.
    const names = Array.from(NAME_WORDS).map((v) => a.src.value(v).trim().toLowerCase());
    const seen = new Set<string>([current, ...names]);
    const out: Array<Record<string, string>> = [];
    const family = familyMembers(main).filter((v) => !NAME_WORDS.has(v));
    family.map((v) => a.src.value(v)).forEach((value) => {
        const low = value.trim().toLowerCase();
        if (!low || seen.has(low)) return;
        seen.add(low);
        if (others.some((t) => t.includes(low))) return;
        out.push({ [main]: value.trim() });
    });
    return out;
}

/** The row's main word, as wordCandidates picks it from the template. */
function mainVar(row: AssembledRow, vars: string[]): string | undefined {
    const ctx = rowCtx(row);
    return vars.find((v) => !PLACE_VARS.has(v) && ctx[v] === undefined && !NAME_WORDS.has(v) && wordFamily(v) !== "language");
}

/** What a row asks about: its main word's value, "" when it asks about the place itself. */
function subjectOf(row: AssembledRow): string {
    const main = mainVar(row, row.question.fill_vars);
    return main ? (row.question.fill_values[main] ?? "").trim().toLowerCase() : "";
}

/**
 * Score `after` for the row at `key`: null when the candidate fails a check,
 * else its highest similarity to any other row (lower is better). With
 * `subject`, the row must still ask about it.
 */
function score(before: Assembly, after: Assembly, key: string, n: number, subject?: string): number | null {
    if (!better(before, after, key, n)) return null;
    const row = after.rows.find((r) => r.plan.key === key);
    if (!row) return null;
    if (subject !== undefined && subjectOf(row) !== subject) return null;
    const others = after.rows.filter((r) => r !== row);
    if (others.some((r) => sameMeaning(r.question.text, row.question.text))) return null;
    return others.reduce((m, r) => Math.max(m, similarity(r.question.text, row.question.text)), 0);
}

function tryRow(work: DraftState, a: Assembly, row: AssembledRow): boolean {
    const key = row.plan.key;
    const n = row.question.n;
    const prior = work.auto?.[key];
    const from = prior?.from ?? row.question.text;
    const used = usedTemplates(work, a);
    const home = work.built.buy === "we_go_to_them";
    const auto = work.auto as Record<string, AutoReword>;
    const subject = subjectOf(row);
    const pickBest = (candidates: AutoReword[], keepSubject: boolean): AutoReword | null => {
        let best: { c: AutoReword; s: number } | null = null;
        candidates.forEach((c) => {
            auto[key] = c;
            const s = score(a, assemble(work), key, n, keepSubject ? subject : undefined);
            if (s !== null && (!best || s < best.s)) best = { c, s };
        });
        return best ? (best as { c: AutoReword; s: number }).c : null;
    };
    const templates = templatePool(row, a.rows, home)
        .filter((t) => t.check === row.plan.check && !used.has(t.id) && !(home && VISIT_ONLY.test(t.text)))
        .map((t): AutoReword => ({ tpl: t.id, from }));
    let chosen = pickBest(templates, true);
    if (!chosen) {
        const words = wordCandidates(a, row, prior).map((values): AutoReword => ({ ...(prior?.tpl ? { tpl: prior.tpl } : {}), values, from }));
        chosen = pickBest(words, false);
    }
    if (chosen) {
        auto[key] = chosen;
        return true;
    }
    if (prior) auto[key] = prior;
    else delete auto[key];
    return false;
}

/**
 * Settle the repeats Oura can, in place on `state.auto`. A recipe change runs
 * on its preview (every row on the new recipe), never on the frozen set.
 * Rows a staff member locked keep the wording the pass gave them before.
 */
export function dedupePass(state: DraftState): void {
    // Brand rows are transcribed from Google searches, never templated: a repeat there is staff's call.
    if (state.built.buy === "online") {
        state.auto = {};
        return;
    }
    const kept: Record<string, AutoReword> = {};
    Object.entries(state.auto ?? {}).forEach(([key, x]) => {
        if (state.rows[key]?.locked) kept[key] = x;
    });
    const work: DraftState = { ...state, auto: kept, oldKeys: state.kind === "recipe_change" ? [] : state.oldKeys };
    const gaveUp = new Set<string>();
    for (let round = 0; round < MAX_ROUNDS; round++) {
        const a = assemble(work);
        const byKey = new Map(a.rows.map((r) => [r.plan.key, r]));
        const byN = new Map(a.rows.map((r) => [r.question.n, r]));
        const dup = a.issues.find((i) => isDup(i) && !gaveUp.has(pairId(i)));
        if (!dup) break;
        const later = byKey.get(dup.key);
        const earlier = dup.other ? byN.get(dup.other) : undefined;
        const order = !later || !earlier ? [] : later.plan.check === "location" && earlier.plan.check !== "location" ? [earlier, later] : [later, earlier];
        const fixed = order.some((row) => changeable(work, row) && tryRow(work, a, row));
        if (!fixed) gaveUp.add(pairId(dup));
    }
    state.auto = work.auto;
}
